import { useContext, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { create } from 'zustand'
import ConsumerAPI from '../services/my-account/consumer-sdk/consumer.api'
import { UserDataContext } from '../services/user/user-data'
import { UserTypeLauDecorations } from '../types/visibility-gate'
import { GIGYA_COOKIE_JWT, GIGYA_COOKIE_UID } from '../utils/constants'
import { CbxConfigType, PageConfig } from '../utils/page-config'

interface PointsBalanceState {
  pointsBalance: number
  membershipGroup: string
  tierLevel: string
  tierPointsToNextLevel: number
  tierTotalPoints: number
  isLoadingPointsBalance: boolean
  isError: boolean
}

interface FetchArgs {
  jwt?: string
  uid?: string
}

interface PointsBalanceStore extends PointsBalanceState {
  isFetching: boolean
  lastFetchedKey: string | null
  fetchPoints: (args: FetchArgs, force?: boolean) => Promise<void>
  setPointsBalance: (value: number) => void
}

const buildIdentityKey = ({ jwt, uid }: FetchArgs): string =>
  `${jwt ?? ''}|${uid ?? ''}`

const initialState: PointsBalanceState = {
  pointsBalance: 0,
  membershipGroup: '',
  tierLevel: '0',
  tierPointsToNextLevel: 0,
  tierTotalPoints: 0,
  isLoadingPointsBalance: true,
  isError: false
}

const LOYALTY_ENDPOINT = '/swma-loyalty/v1.0'

function buildHeaders(cbxConfig: CbxConfigType, jwt?: string): Headers {
  const headers = new Headers({
    'X-Brand': cbxConfig?.brand ?? '',
    'X-Country-Code': cbxConfig?.xCountryCode ?? '',
    'X-Channel': cbxConfig?.xChannel ?? '',
    'X-Application-Id': cbxConfig?.applicationId ?? '',
    'X-Market': cbxConfig?.xMarket ?? '',
    'X-Locale': cbxConfig?.locale ?? ''
  })

  if (jwt) {
    headers.set('Authorization', `Bearer ${jwt}`)
  }

  return headers
}

/**
 * Shared Zustand store holding the loyalty points/tier state.
 *
 * State lives in a single module-level store so every consumer of
 * usePointsBalance shares one instance and one fetch, and updates made via
 * setPointsBalance / refreshPointsBalance propagate to all consumers.
 */
const usePointsBalanceStore = create<PointsBalanceStore>((set, get) => ({
  ...initialState,
  isFetching: false,
  lastFetchedKey: null,

  fetchPoints: async (args: FetchArgs, force = false) => {
    const { jwt, uid } = args
    const key = buildIdentityKey(args)
    const { isFetching, lastFetchedKey } = get()

    // Deduplicate: multiple consumers share one store, so skip if a request
    // for this identity is already in flight or has already completed —
    // unless a caller explicitly forces a refresh.
    if (!force && (isFetching || lastFetchedKey === key)) {
      return
    }

    const cbxConfig = PageConfig?.getConfig(
      PageConfig?.ConfigObjects?.CbxConfig
    ) as CbxConfigType

    set({ isLoadingPointsBalance: true, isError: false, isFetching: true })

    try {
      const consumerId = uid ? atob(uid) : ''
      const data = await ConsumerAPI.getAccount({
        baseUrl: cbxConfig?.cbxUrl ?? '',
        consumerId,
        loyaltyEndpoint: LOYALTY_ENDPOINT,
        headers: buildHeaders(cbxConfig, jwt)
      })

      set({
        pointsBalance: data?.availablePointsBalance ?? 0,
        membershipGroup: data?.membershipGroup ?? '',
        tierLevel: data?.tierLevel ?? '0',
        tierPointsToNextLevel: data?.tierPointsToNextLevel ?? 0,
        tierTotalPoints: data?.tierTotalPoints ?? 0,
        isLoadingPointsBalance: false,
        isError: false,
        isFetching: false,
        lastFetchedKey: key
      })
    } catch {
      // Leave lastFetchedKey null so a failed identity can be retried
      // (e.g. by refreshPointsBalance or a later remount).
      set({
        ...initialState,
        isLoadingPointsBalance: false,
        isError: true,
        isFetching: false,
        lastFetchedKey: null
      })
    }
  },

  setPointsBalance: (value: number) => set({ pointsBalance: value })
}))

/**
 * Resets the shared store to its initial state.
 *
 * Intended for tests, where the module-level singleton would otherwise leak
 * state between cases.
 */
export const resetPointsBalanceStore = (): void => {
  usePointsBalanceStore.setState({
    ...initialState,
    isFetching: false,
    lastFetchedKey: null
  })
}

interface UsePointsBalance extends PointsBalanceState {
  setPointsBalance?: (value: number) => void
  refreshPointsBalance?: () => void
}

/**
 * Public hook — same API as before.
 *
 * Reads the Gigya cookies in React land, lazily triggers a single fetch on
 * first consumption (and re-fetches when the cookies change), and returns the
 * shared store state together with stable setPointsBalance / refreshPointsBalance
 * callbacks.
 */
const usePointsBalance = (): UsePointsBalance => {
  const [cookies] = useCookies([GIGYA_COOKIE_JWT, GIGYA_COOKIE_UID])
  const userData = useContext(UserDataContext)

  const pointsBalance = usePointsBalanceStore((state) => state.pointsBalance)
  const membershipGroup = usePointsBalanceStore(
    (state) => state.membershipGroup
  )
  const tierLevel = usePointsBalanceStore((state) => state.tierLevel)
  const tierPointsToNextLevel = usePointsBalanceStore(
    (state) => state.tierPointsToNextLevel
  )
  const tierTotalPoints = usePointsBalanceStore(
    (state) => state.tierTotalPoints
  )
  const isLoadingPointsBalance = usePointsBalanceStore(
    (state) => state.isLoadingPointsBalance
  )
  const isError = usePointsBalanceStore((state) => state.isError)
  const setPointsBalance = usePointsBalanceStore(
    (state) => state.setPointsBalance
  )

  const jwt = cookies.gig_jwt
  const uid = cookies.gig_uid

  // Loyalty points only exist for Hard Age Verified users. LAS (anonymous) and
  // LAU (logged in, not HAV verified) users must never trigger the loyalty
  // membership call — e.g. on the HAV form page.
  const decoratedUserType = userData?.decoratedUserType
  const isHavVerified =
    decoratedUserType === UserTypeLauDecorations.LauHav ||
    decoratedUserType === UserTypeLauDecorations.LauHavDev

  useEffect(() => {
    // Deduplicated inside the store: only the first consumer for a given
    // identity triggers the network call, even when several components
    // mount usePointsBalance on the same page. Gated on HAV status so
    // non-verified users never hit the loyalty endpoint.
    if (jwt && uid && isHavVerified) {
      usePointsBalanceStore.getState().fetchPoints({ jwt, uid })
    }
  }, [jwt, uid, isHavVerified])

  const refreshPointsBalance = () => {
    usePointsBalanceStore.getState().fetchPoints({ jwt, uid }, true)
  }

  return {
    pointsBalance,
    membershipGroup,
    tierLevel,
    tierPointsToNextLevel,
    tierTotalPoints,
    isLoadingPointsBalance,
    isError,
    setPointsBalance,
    refreshPointsBalance
  }
}

export default usePointsBalance
